import { createChat, uploadChatImage } from './chats';

export interface CrashError {
  type: string;
  message: string;
  stack: string;
}

export interface CrashReport {
  gameVersionId: string | null;
  error: CrashError;
  /** A PNG data URL; null when the capture failed or came out blank. */
  screenshot: string | null;
}

export const CrashDescriptionMaxLength = 2000;

const GameChannel = 'entire-game';
const CrashType = 'crash';
const SiteChannel = 'entire-site';
const AcknowledgementType = 'crash-ack';
const ChatName = 'Crash report';
const GameCode = 'EntireGame.Core';
const MaxStackLines = 40;
const MaxTypeLength = 200;
const MaxMessageLength = 2000;
const MaxStackLength = 20000;
const MaxStackLineLength = 300;
const MaxVersionIdLength = 64;
const PngDataUrlPrefix = 'data:image/png;base64,';
const MaxScreenshotBytes = 5 * 1024 * 1024;
const MaxScreenshotLength = PngDataUrlPrefix.length + Math.floor(MaxScreenshotBytes / 3) * 4;

export function crashReportOf(event: MessageEvent, frame: Window | null, origin: string): CrashReport | null {
  if (!frame || event.source !== frame || event.origin !== origin) return null;

  const data = event.data as Record<string, unknown> | null;
  if (!data || typeof data !== 'object' || data.source !== GameChannel || data.type !== CrashType) return null;

  const error = data.error as Record<string, unknown> | null;
  if (!error || typeof error !== 'object' || Array.isArray(error)) return null;

  return {
    gameVersionId: versionIdOf(data.gameVersionId),
    error: {
      type: clipped(textOf(error.type), MaxTypeLength),
      message: clipped(textOf(error.message), MaxMessageLength),
      stack: clipped(textOf(error.stack), MaxStackLength),
    },
    screenshot: screenshotOf(data.screenshot),
  };
}

export function acknowledgeCrash(frame: Window, origin: string): void {
  frame.postMessage({ source: SiteChannel, type: AcknowledgementType }, origin);
}

export function crashHeadline(error: CrashError): string {
  return error.type ? `${error.type}: ${error.message}` : error.message;
}

export function crashReportStack(error: CrashError): string {
  const lines = error.stack
    .split(/\r?\n/)
    .map((line) => clipped(line.trim(), MaxStackLineLength))
    .filter(Boolean);
  if (lines.length === 0) return '(not available)';
  if (lines.length <= MaxStackLines) return lines.join('\n');

  const kept = new Set<number>();
  for (const [index, line] of lines.entries()) {
    if (kept.size < MaxStackLines && line.includes(GameCode)) kept.add(index);
  }
  for (const index of lines.keys()) {
    if (kept.size < MaxStackLines) kept.add(index);
  }
  const shown = lines.filter((_, index) => kept.has(index));
  return [...shown, `(${lines.length - shown.length} more lines not shown)`].join('\n');
}

export function crashReportFirstMessage(error: CrashError, version: string | null, description: string): string {
  return [
    `The game crashed while I was playing${version ? ` version ${version}` : ''}.`,
    `Error: ${crashHeadline(error)}`,
    'Stack:',
    crashReportStack(error),
    `What I was doing: ${clipped(description.trim(), CrashDescriptionMaxLength) || '(not described)'}`,
    'Please find the cause and fix the game so this cannot happen again.',
  ].join('\n');
}

export async function sendCrashReport(
  projectId: string,
  report: CrashReport,
  version: string | null,
  description: string,
): Promise<void> {
  const imageId = await uploadedScreenshot(projectId, report.screenshot);
  await createChat(
    projectId,
    crashReportFirstMessage(report.error, version, description),
    ChatName,
    imageId ? [imageId] : undefined,
  );
}

function textOf(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function clipped(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

function versionIdOf(value: unknown): string | null {
  return typeof value === 'string' && value && value.length <= MaxVersionIdLength ? value : null;
}

function screenshotOf(value: unknown): string | null {
  return typeof value === 'string' && value.length <= MaxScreenshotLength && value.startsWith(PngDataUrlPrefix)
    ? value
    : null;
}

async function uploadedScreenshot(projectId: string, screenshot: string | null): Promise<string | null> {
  const file = screenshot ? pngFileOf(screenshot) : null;
  if (!file) return null;
  try {
    return (await uploadChatImage(projectId, file)).imageId;
  } catch (error) {
    console.warn('The crash screenshot did not upload; the fix request goes without it.', error);
    return null;
  }
}

function pngFileOf(dataUrl: string): File | null {
  try {
    const binary = atob(dataUrl.slice(PngDataUrlPrefix.length));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new File([bytes], 'crash-screenshot.png', { type: 'image/png' });
  } catch {
    return null;
  }
}
